// The default current streaks follow the depth bands (R2871;
// ui-map-interface.md §17.3.07).
//
// The depth tints lay the deep ink over the water at a stronger alpha on
// each deeper band, so the ground under a streak gets darker. A pale streak
// at one alpha then stands out more on the abyss than on the shelf. While
// the tints draw, each streak, mote and stream atlas arrow takes a gain of
// the band under it, and the gain holds the OKLab lightness step of the
// mark over the band composite equal to its step over the bare water.
//
// The bare water is the ground the owner picked the looks on (round 117),
// and the shelf tint has alpha 0 in both depth looks, so the shelf keeps
// gain 1. The gains are solved from the depth figures the map draws, so a
// tune of `[chart.piece.depth_tints]` moves them with no second table.
//
// The module is pure: no GL and no DOM, so the tests read the solve.

import { WATER_DEEP_TOP } from '../../../gen/presentation';
import { bandAt } from '../../../deep';
import { depthLookCfg, type DepthLook } from '../../../depthlook';
import { channels, packHex } from '../../../render/color';
import { rgbToOklab, type Rgb } from '../../../ui/theme';
import { cellAt } from '../../../world';
import type { ChartReading } from '../context';
import { CHART_INK } from './shared';

/** The water the tints lie on: the top of the deep gradient, the ground
 *  the legend swatches use too. */
export const STREAK_WATER = packHex(WATER_DEEP_TOP);

/** `top` over `under` at `alpha`, unrounded: the blend the GPU does on
 *  the sRGB values of a premultiplied mark. */
function mix(top: Rgb, under: Rgb, alpha: number): Rgb {
  return [0, 1, 2].map((i) => top[i] * alpha + under[i] * (1 - alpha)) as Rgb;
}

/** OKLab lightness of an sRGB triplet. */
function okL(c: Rgb): number {
  return rgbToOklab(c)[0];
}

/** The ground under a streak on one band: the deep ink of the tint at its
 *  alpha over the water. */
export function bandGround(tint: number, water = STREAK_WATER, ink = CHART_INK.deep): Rgb {
  return mix(channels(ink), channels(water), tint);
}

/** The lightness step of a mark of `ink` at `alpha` over `ground`. */
export function markStep(ink: number, alpha: number, ground: Rgb): number {
  return okL(mix(channels(ink), ground, alpha)) - okL(ground);
}

/**
 * The alpha gain of a mark of `ink` at `alpha` on each band, index 0 (no
 * band) to 4 (abyss): the gain whose step over the band ground equals the
 * step over the bare water. Each gain is solved by bisection, and a gain
 * stops where the mark would pass alpha 1.
 */
export function streakBandGains(
  ink: number, alpha: number, tints: readonly number[], water = STREAK_WATER,
): number[] {
  const out = [1, 1, 1, 1, 1];
  if (!(alpha > 0)) return out;
  const want = markStep(ink, alpha, channels(water));
  const hi = 1 / alpha;
  for (let b = 1; b <= 4; b++) {
    const t = tints[b - 1] ?? 0;
    if (!(t > 0)) continue;
    const ground = bandGround(t, water);
    let lo = 0, up = hi;
    for (let k = 0; k < 40; k++) {
      const m = (lo + up) / 2;
      if (Math.abs(markStep(ink, alpha * m, ground)) < Math.abs(want)) lo = m; else up = m;
    }
    out[b] = (lo + up) / 2;
  }
  return out;
}

/**
 * The gains the piece reads, cached for the ink, the alpha, the reading
 * and the depth look, so a frame solves nothing. `null` while the depth
 * tints do not draw: every mark then takes gain 1.
 */
export class BandGains {
  private gains: number[] = [1, 1, 1, 1, 1];
  private bands: Uint8Array | null = null;
  /** What the gains were solved for; no string key, so a frame allocates
   *  nothing (§10.3). */
  private look: DepthLook | null = null;
  private reading: ChartReading | null = null;
  private ink = -1;
  private alpha = -1;

  /** Takes the context of this frame: the band field, and the look the
   *  tints draw in, or null while no tint draws. */
  set(bands: Uint8Array | null, look: DepthLook | null, reading: ChartReading, ink: number, alpha: number): void {
    this.bands = look === null ? null : bands;
    if (look === null || bands === null) return;
    if (look === this.look && reading === this.reading && ink === this.ink && alpha === this.alpha) return;
    this.look = look;
    this.reading = reading;
    this.ink = ink;
    this.alpha = alpha;
    this.gains = streakBandGains(ink, alpha, depthLookCfg(look, reading).alphas);
  }

  /** The gain of a mark at a world point: 1 off the tints. */
  at(x: number, y: number): number {
    const bands = this.bands;
    if (bands === null) return 1;
    return this.gains[bandAt(bands, cellAt(x, y))] ?? 1;
  }
}
